import { useEffect, useState } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { z } from "zod";
import {
  updateClass,
  getClassById,
  getAllLevel,
} from "../../../api/pedagogicElement";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

//Typage des props parents
type UUID = string;
type Props = {
  show: boolean;
  classID: UUID;
  onClose: () => void;
  onSaved?: () => void;
};

interface Level {
  id: string;
  label: string;
}

const EditClassModal = ({ classID, show, onClose, onSaved }: Props) => {
  //Initialisation de l'année academmique actuelle
  const { selectedYear, setSelectedYear } = useAcademicYear();
  const [levels, setLevels] = useState<Level[]>([]);

  //Schéma de notre formulaire
  const createSchema = z.object({
    label: z.string().nonempty("Le libellé de la classe est requis"),
    abbreviation: z.string().nonempty("L'abréviation est requis"),
    domain: z.string().nonempty("Le domaine est requis"),
    mention: z.string().nonempty("La mention est requise"),
    id_level: z.string().nonempty("Le niveau d'étude est requis"),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour création vers le backend
      await updateClass(data, classID);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Classe mise à jour !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      /// Si erreur (Toast Error)
      toast.error(
        error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!show || !classID) return; // ✅ Ne rien faire si le modal n'est pas ouvert

    const fetchClass = async () => {
      try {
        const response = await getClassById(classID);
        if (!response) {
          // Si aucune année trouvé
          throw new Error("Matière introuvable");
        }

        reset({
          // Si année trouvé on rempli les champs du formulaire
          label: response.data.label,
          abbreviation: response.data.abbreviation,
          domain: response.data.domain,
          mention: response.data.mention,
          id_level: response.data.id_level,
        });
      } catch (error: any) {
        // Si erreur (Toast Error)
        toast.error(
          error.message ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Une erreur s'est produit, Réessayer plus tard",
        );
      }
    };

    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;

    const fetchLevel = async () => {
      try {
        const level = await getAllLevel(
          selectedYear?.id,
          buildPermissionContext(Resource.CLASS, Action.UPDATE),
        );
        setLevels(level.data);
        fetchClass();
      } catch (error: any) {
        // Si erreur (Toast Error)
        toast.error(
          error.message || "Une erreur s'est produit, Réessayer plus tard",
        );
      }
    };

    fetchLevel();
  }, [show, classID, reset]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Modifer une Classe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Niveau de la classe</label>
                  <select
                    {...register("id_level")}
                    aria-label="Default select example"
                    className={
                      errors.id_level ? "form-select is-invalid" : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    {levels.map((level) => (
                      <option key={level.id} value={level.id}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                  {errors.id_level && (
                    <div className="invalid-feedback">
                      {errors.id_level.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Business Intelligence"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Abréviation
                  </label>
                  <input
                    {...register("abbreviation")}
                    type="text"
                    className={
                      errors.abbreviation
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: BI"
                  />
                  {errors.abbreviation && (
                    <div className="invalid-feedback">
                      {errors.abbreviation.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Domaine
                  </label>
                  <input
                    {...register("domain")}
                    type="text"
                    className={
                      errors.domain ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Sciences Informatiques"
                  />
                  {errors.domain && (
                    <div className="invalid-feedback">
                      {errors.domain.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Mention
                  </label>
                  <input
                    {...register("mention")}
                    type="text"
                    className={
                      errors.mention
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: Informatique"
                  />
                  {errors.mention && (
                    <div className="invalid-feedback">
                      {errors.mention.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditClassModal,
  [buildPermission(Resource.CLASS, Action.UPDATE)],
  "masquer",
);
